import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  AlertCircle, 
  CheckCircle2, 
  Stethoscope, 
  ClipboardList,
  Phone,
  Clock
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { publicAPI } from '../services/api';
import { Button } from '../components/ui/button';

// Helper function to render text with markdown-style bold (**text**)
const renderFormattedText = (text) => {
  if (!text) return null;
  
  // Replace **text** with <strong>text</strong>
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={`item-${index}`} className="font-semibold text-gray-800">{part.slice(2, -2)}</strong>;
    }
    // Replace bullet points
    if (part.startsWith('• ')) {
      return <span key={`item-${index}`}>{part}</span>;
    }
    return part;
  });
};

const TestDetailPage = () => {
  const { slug } = useParams();
  const [testData, setTestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await publicAPI.getTestDetailBySlug(slug);
        setTestData(response.data);
      } catch (err) {
        console.error('Error fetching test detail:', err);
        setError('Test information not found');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#007897]"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !testData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Test Not Found</h1>
          <p className="text-gray-600 mb-6">{error || 'The test you are looking for does not exist.'}</p>
          <Link to="/services">
            <Button className="bg-[#007897] hover:bg-[#006680]">
              <ArrowLeft className="mr-2" size={18} />
              Back to Services
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <FloatingButtons />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center text-sm text-gray-600">
            <Link to="/" className="hover:text-[#007897]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/services" className="hover:text-[#007897]">Services</Link>
            {testData.service_category && (
              <>
                <span className="mx-2">/</span>
                <Link 
                  to={`/services/${testData.service_category.toLowerCase().replace(/\s+/g, '-')}`}
                  className="hover:text-[#007897]"
                >
                  {testData.service_category}
                </Link>
              </>
            )}
            <span className="mx-2">/</span>
            <span className="text-gray-900 font-medium">{testData.name}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#007897] to-[#3949ab] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-start gap-8">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                {testData.service_category && (
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm">
                    {testData.service_category}
                  </span>
                )}
                {testData.sub_service && (
                  <span className="bg-white/10 px-3 py-1 rounded-full text-sm">
                    {testData.sub_service}
                  </span>
                )}
              </div>
              <h1 className="text-3xl lg:text-4xl font-bold mb-4">{testData.name}</h1>
              <p className="text-lg text-white/90 mb-6">{testData.short_description}</p>
              
              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-white text-[#007897] hover:bg-gray-100">
                    <Phone size={18} className="mr-2" />
                    Contact Us
                  </Button>
                </a>
              </div>
            </div>
            
            {testData.image && (
              <div className="lg:w-1/3">
                <img 
                  src={testData.image} 
                  alt={testData.name}
                  className="rounded-2xl shadow-xl w-full h-64 object-cover"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About the Test */}
              {testData.full_description && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#007897]/10 rounded-lg flex items-center justify-center">
                      <FileText className="text-[#007897]" size={20} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">About the Test</h2>
                  </div>
                  <div className="prose prose-gray max-w-none">
                    {testData.full_description.split('\n').map((paragraph, idx) => (
                      paragraph.trim() && (
                        <p key={`item-${idx}`} className="text-gray-600 mb-4 leading-relaxed">
                          {renderFormattedText(paragraph)}
                        </p>
                      )
                    ))}
                  </div>
                </div>
              )}

              {/* Why Get Tested */}
              {testData.why_get_tested && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                      <Stethoscope className="text-green-600" size={20} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Why Get This Test?</h2>
                  </div>
                  <div className="prose prose-gray max-w-none">
                    {testData.why_get_tested.split('\n').map((paragraph, idx) => (
                      <p key={`item-${idx}`} className="text-gray-600 mb-4 leading-relaxed">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {/* Conditions Detected */}
              {testData.conditions_detected && testData.conditions_detected.length > 0 && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <AlertCircle className="text-blue-600" size={20} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Conditions This Test Helps Detect</h2>
                  </div>
                  <div className="grid md:grid-cols-2 gap-3">
                    {testData.conditions_detected.map((condition, idx) => (
                      <div key={`item-${idx}`} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                        <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-700">{condition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Procedure */}
              {testData.procedure && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                      <ClipboardList className="text-purple-600" size={20} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">What Happens During the Test?</h2>
                  </div>
                  <div className="prose prose-gray max-w-none">
                    {testData.procedure.split('\n').map((paragraph, idx) => (
                      paragraph.trim() && (
                        <p key={`item-${idx}`} className="text-gray-600 mb-4 leading-relaxed">
                          {renderFormattedText(paragraph)}
                        </p>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6">
              {/* Report Time Card - Hidden for Laboratory Services and Radiology (Ultrasound/Doppler) */}
              {testData.service_category && 
               !testData.service_category.toLowerCase().includes('laboratory') && 
               !testData.service_category.toLowerCase().includes('radiology') && (
                <div className="bg-[#007897] text-white rounded-2xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Clock size={24} />
                    <h3 className="text-lg font-bold">Report Time</h3>
                  </div>
                  <p className="text-2xl font-bold">{testData.reporting_time || 'Same Day'}</p>
                  <p className="text-sm text-white/80 mt-1">Results available within this time frame</p>
                </div>
              )}

              {/* Patient Instructions */}
              {testData.instructions && testData.instructions.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-amber-800 mb-4 flex items-center gap-2">
                    <AlertCircle size={20} />
                    Patient Instructions
                  </h3>
                  <ul className="space-y-3">
                    {testData.instructions.map((instruction, idx) => (
                      <li key={`item-${idx}`} className="flex items-start gap-3 text-amber-900">
                        <span className="bg-amber-200 text-amber-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-medium">
                          {idx + 1}
                        </span>
                        <span className="text-sm">{instruction}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TestDetailPage;
